import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * PageHeader - Encabezado universal para las páginas del sistema.
 * 
 * @param {string} title - Título principal de la página.
 * @param {string|React.ReactNode} subtitle - Subtítulo o texto descriptivo.
 * @param {Array} buttons - Lista de botones customizados [{ label, onClick, icon, variant, to }]
 * @param {boolean} showBackButton - Si es true, muestra botón por defecto 'Volver al Cpanel' (o a donde indique 'backTo').
 * @param {string} backTo - Ruta personalizada para el botón de regresar (Por defecto: '/cpanel').
 * @param {string} topBorderGradient - Clases de Tailwind para la línea superior opcional (ej: 'from-blue-600 via-indigo-600 to-purple-600').
 * @param {React.ReactNode} rightContent - Badge o elemento customizado para la esquina superior derecha.
 */
const PageHeader = ({
    title,
    subtitle,
    buttons = [],
    showBackButton = true,
    backTo = '/cpanel',
    topBorderGradient = null,
    rightContent = null,
}) => {
    const navigate = useNavigate();

    return (
        <header className="bg-white border-b border-slate-200/80 shadow-2xs sticky top-0 z-30 shrink-0 w-full select-none">
            {/* LÍNEA DE COLOR OPCIONAL */}
            {topBorderGradient && (
                <div className={`h-1.5 bg-gradient-to-r ${topBorderGradient} w-full`} />
            )}

            <div className="w-full px-8 py-4 flex items-center justify-between gap-4">
                
                {/* LADO IZQUIERDO: LOGO + SEPARADOR + TÍTULO */}
                <div className="flex items-center gap-4 min-w-0">
                    <img 
                        src="/imagenes/Logo.svg" 
                        alt="Logo IB" 
                        className="h-14 w-auto object-contain shrink-0" 
                    />
                    <div className="h-8 w-px bg-slate-200 hidden sm:block shrink-0" />
                    
                    <div className="truncate">
                        <h1 className="text-xl font-black text-slate-900 tracking-tight leading-none truncate">
                            {title}
                        </h1>
                        {subtitle && (
                            <span className="text-[12px] text-slate-400 font-medium tracking-wide block mt-1 truncate">
                                {subtitle}
                            </span>
                        )}
                    </div>
                </div>

                {/* LADO DERECHO: ACCIONES (BOTONES Y/O BADGES) */}
                <div className="flex items-center gap-3 shrink-0">
                    
                    {/* Elementos extra a la derecha (ej: Badges tipo "Finanzas Pro") */}
                    {rightContent}

                    {/* Botones pasados por props */}
                    {buttons.map((btn, index) => (
                        <button
                            key={index}
                            onClick={btn.onClick || (() => btn.to && navigate(btn.to))}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-xs transition-all shadow-2xs cursor-pointer border ${
                                btn.variant === 'primary'
                                    ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                                    : btn.variant === 'danger'
                                    ? 'bg-white text-rose-600 border-rose-200 hover:bg-rose-50'
                                    : 'bg-white text-slate-700 border-slate-200/90 hover:bg-slate-50 hover:border-slate-300'
                            }`}
                        >
                            {btn.icon}
                            <span>{btn.label}</span>
                        </button>
                    ))}

                    {/* Botón Volver por defecto */}
                    {showBackButton && buttons.length === 0 && (
                        <button 
                            onClick={() => navigate(backTo)}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200/90 text-slate-700 font-bold hover:bg-slate-50 hover:border-slate-300 transition-all text-xs bg-white shadow-2xs cursor-pointer"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            <span>Volver al Cpanel</span>
                        </button>
                    )}

                </div>

            </div>
        </header>
    );
};

export default PageHeader;